import { useContext, useEffect, useState } from "react";

import _ from "lodash";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { ddqDescriptionText, formSectionsPerPage } from "./common";
import DDQPDFDocument from "./DDQPDFDocument";

import { trackDDQUIViewAction } from "~/analytics";
import * as Breadcrumbs from "~/components/Breadcrumbs";
import { Box, Padding } from "~/components/Elements";
import * as Grid from "~/components/Grid";
import { ArrowLeftIcon } from "~/components/icons/Arrow";
import Pagination from "~/components/Pagination";
import { Title } from "~/components/Text";
import { BackButtonContext } from "~/contexts/BackButtonContext";
import { useDDQDocument } from "~/endpoints/trustFramework";
import useCurrentViewer from "~/hooks/useCurrentViewer";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import useVaspContext from "~/hooks/useVaspContext";
import DashboardFocusLayout from "~/layouts/DashboardFocus";

type Props = {
  ownerVASPDID: string;
  ownerVASPName: string;
  isSinglePage?: boolean; // single page form without pagination
  onError?: (error: string) => void;
  onGoBack?: () => void;
};

const DDQDiv = styled.div`
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 800;
  font-size: 14px;
  line-height: 17px;
  color: #72e85f;
`;

export default function DDQView({ isSinglePage = false, ownerVASPDID, ownerVASPName, onError, onGoBack }: Props) {
  const router = useRouter();
  const requestingVASPDID = useCurrentViewerVaspDID();
  const { viewer } = useCurrentViewer();
  const { data: ddqValues, isLoading, error } = useDDQDocument(
    ownerVASPDID,
    ownerVASPDID === requestingVASPDID ? undefined : requestingVASPDID,
  );
  const { ddqBack } = useContext(BackButtonContext);
  const vaspContext = useVaspContext();
  const activeVasp = vaspContext.getActiveVasp();

  const [page, setPage] = useState(parseInt(String(router.query.currentPage)) || 0);

  useEffect(() => {
    setPage(parseInt(String(router.query.currentPage)) || 0);
  }, [router.query.currentPage]);

  useEffect(() => window.scrollTo({ top: 0, behavior: "smooth" }), [page]);

  const form = useForm({
    shouldUnregister: false,
    defaultValues: ddqValues?.data || {},
  });

  useEffect(() => {
    if (!activeVasp) {
      return;
    }
    trackDDQUIViewAction({
      userDID: viewer.did,
      vaspDID: requestingVASPDID,
      vaspName: String(activeVasp.name),
      ownerVASPDID,
      ownerVASPName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVasp]);

  if (isLoading) {
    return null;
  }

  const onClickGoBack = onGoBack || (() => router.push(ddqBack ? ddqBack : "/dashboard/my-company"));

  if (error) {
    onClickGoBack();
    if (onError) {
      onError(error);
    }
    return null;
  }

  const content = (
    <Grid.Columns multiline>
      <Grid.Column mobile={12}>
        <Breadcrumbs.Container>
          <Breadcrumbs.Content>
            <Breadcrumbs.Item>
              <Breadcrumbs.ItemContent onClick={onClickGoBack}>
                <Breadcrumbs.ItemContentIcon>
                  <ArrowLeftIcon />
                </Breadcrumbs.ItemContentIcon>
                <Breadcrumbs.ItemContentText>Back</Breadcrumbs.ItemContentText>
              </Breadcrumbs.ItemContent>
            </Breadcrumbs.Item>
          </Breadcrumbs.Content>
        </Breadcrumbs.Container>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Title as="h1">Due Diligence Questionnaire</Title>
        </div>
        <div className="mt-4 mb-4">{ddqDescriptionText}</div>
        <div style={{ display: "flex", justifyContent: "right" }} className="pt-4">
          <DDQPDFDocument ownerVASPDID={ownerVASPDID} title={<DDQDiv>Download PDF</DDQDiv>} />
        </div>
      </Grid.Column>
      <Grid.Column mobile={12}>
        <Box>
          <Padding sized="medium">
            {isSinglePage &&
              _.flatten(formSectionsPerPage).map((Section, i) => <Section key={i} mode="read" form={form} />)}
            {!isSinglePage &&
              formSectionsPerPage[page].map((Section, i) => <Section key={i} mode="read" form={form} />)}
          </Padding>
        </Box>
      </Grid.Column>

      {!isSinglePage && (
        <Grid.Column mobile={12}>
          <Padding sized="small">
            <Pagination totalPages={formSectionsPerPage.length} />
          </Padding>
        </Grid.Column>
      )}
    </Grid.Columns>
  );

  return isSinglePage ? content : <DashboardFocusLayout fluidness="mobile">{content}</DashboardFocusLayout>;
}
